// Anita Week 3 / Day 1 — Measuring the World: Length, Mass and Volume.
// Content is supplied verbatim by the curriculum author; this file only places
// that content into the existing Nimny Lab lesson schema. No pedagogy is invented.
// SAFETY: only safe household objects, no glass, no sharp tools, no heavy objects.

const day1 = {
  id: 'day-1',
  studentId: 'anita',
  weekId: 'week-3',
  dayId: 'day-1',
  status: 'active',
  title: 'Измеряем мир: длина, масса и объём / Measuring the World: Length, Mass and Volume',
  shortTitle: 'Измеряем мир / Measuring the World',
  theme:
    'Анита учится измерять предметы, сравнивать результаты и записывать наблюдения как маленький учёный. / Anita learns to measure objects, compare results, and record observations like a young scientist.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'measurement', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни открывает маленькую лабораторию. На столе лежат линейка, чашка, ложка, книга и карандаш. Сегодня Анита узнает, что учёный не просто смотрит — он измеряет, записывает и сравнивает. / Nimny opens a small lab. On the table there are a ruler, a cup, a spoon, a book and a pencil. Today Anita learns that a scientist does not only look — a scientist measures, records and compares.',
  goals: [
    'Понять, что такое измерение. / Understand measurement.',
    'Измерять длину в сантиметрах. / Measure length in centimeters.',
    'Сравнивать больше, меньше, длиннее, короче, тяжелее, легче. / Compare more, less, longer, shorter, heavier, lighter.',
    'Записывать результат в простую таблицу. / Record results in a simple table.',
    'Прочитать русские слова и короткие фразы. / Read Russian words and short phrases.',
    'Сделать первую лабораторную страницу Нимни. / Create the first Nimny lab page.'
  ],
  vocabulary: [
    { term: 'измерить / measure', definition: 'узнать размер или количество. / find size or quantity.' },
    { term: 'длина / length', definition: 'насколько предмет длинный. / how long an object is.' },
    { term: 'масса / mass', definition: 'насколько предмет тяжёлый. / how heavy an object is.' },
    { term: 'объём / volume', definition: 'сколько помещается внутри. / how much fits inside.' },
    { term: 'линейка / ruler', definition: 'инструмент для измерения длины. / a tool to measure length.' },
    { term: 'сантиметр / centimeter', definition: 'маленькая единица длины. / a small length unit.' },
    { term: 'сравнить / compare', definition: 'посмотреть, что больше, меньше или отличается. / see what is bigger, smaller or different.' },
    { term: 'вывод / conclusion', definition: 'что я понял после наблюдения. / what I understood after observing.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова лаборатории / Lab Words',
      prompt: 'Прочитай по-русски:\nизмерить\nдлина\nмасса\nобъём\nлинейка\nсантиметр\nсравнить\nвывод',
      ruPrimaryText: 'Прочитай 8 русских слов лаборатории.',
      enHelpText: 'Read the Russian lab words first. English is only for help.',
      hint: 'Сначала прочитай сама; родитель может помочь по слогам. / Try first; a parent may help with syllables.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nЯ могу измерить длину.\nЯ могу сравнить два предмета.\nУчёный записывает результат.',
      ruPrimaryText: 'Прочитай 3 короткие фразы по-русски.',
      enHelpText: 'I can measure length. I can compare two objects. A scientist records the result.',
      hint: 'Если слово трудное — попроси родителя помочь по слогам. / Ask a parent for help with syllables if needed.',
      answer: 'Анита пробует читать 3 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'math-length-compare',
      type: 'text',
      title: 'Сравни длину / Compare Length',
      prompt:
        'Нимни измерила предметы:\nкарандаш — 14 см\nкнига — 22 см\nложка — 16 см\n\nОтветь:\n1. Какой предмет самый длинный?\n2. Какой предмет самый короткий?\n3. На сколько сантиметров книга длиннее карандаша?\n4. На сколько сантиметров ложка длиннее карандаша?',
      ruPrimaryText: 'Сравни длину 3 предметов и реши 4 вопроса.',
      enHelpText: 'Compare the length of 3 items: pencil 14 cm, book 22 cm, spoon 16 cm. Answer 4 questions.',
      answer: '1) Книга. 2) Карандаш. 3) 22 − 14 = 8 см. 4) 16 − 14 = 2 см.'
    },
    {
      id: 'math-table-results',
      type: 'text',
      title: 'Таблица измерений / Measurement Table',
      prompt:
        'Заполни ответы по таблице:\nчашка — 9 см\nкоробка — 18 см\nтетрадь — 24 см\n\nВопросы:\n1. Сколько всего сантиметров у чашки и коробки вместе?\n2. На сколько тетрадь длиннее коробки?\n3. Если измерить ещё карандаш 12 см, сколько будет коробка + карандаш?',
      ruPrimaryText: 'Используй таблицу и реши 3 вопроса.',
      enHelpText: 'Use the table (cup 9 cm, box 18 cm, notebook 24 cm) and answer 3 questions.',
      answer: '1) 9 + 18 = 27 см. 2) 24 − 18 = 6 см. 3) 18 + 12 = 30 см.'
    },
    {
      id: 'science-measure-home',
      type: 'text',
      title: 'Маленькое измерение / Small Measurement',
      prompt:
        'Выбери 2 безопасных предмета дома или рядом с родителем.\nИзмерь или оцени их длину.\nЗапиши:\nПредмет 1: ______ см.\nПредмет 2: ______ см.\nЧто длиннее?\n\nВажно: если нет линейки, можно сравнить словами — длиннее, короче, примерно одинаково.',
      ruPrimaryText: 'Безопасно измерь или сравни 2 предмета и запиши результат.',
      enHelpText: 'Pick 2 safe objects with a parent. Measure or estimate. Write what is longer. If no ruler — compare with words.',
      hint: 'Только безопасные предметы. Не использовать стекло или острые предметы. / Safe objects only.',
      answer: 'Open-ended. Parent checks that Anita compares two safe objects.'
    },
    {
      id: 'spelling-measure',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши:\nдлина\nмасса\nвывод\nлинейка\n\nВставь буквы:\nдл_на\nма_са\nвыв_д\nлине_ка',
      ruPrimaryText: 'Напиши 4 слова и вставь пропущенные буквы.',
      enHelpText: 'Write 4 words: length, mass, conclusion, ruler. Then fill the missing letters.',
      answer: 'длина, масса, вывод, линейка.'
    },
    {
      id: 'creative-lab-page',
      type: 'text',
      title: 'Лабораторная страница Нимни / Nimny Lab Page',
      prompt:
        'Сделай лабораторную страницу.\nНарисуй 2 предмета.\nНапиши:\nЯ измерила ______.\nОн/она был(а) ______ см.\nМой вывод: ______ длиннее.',
      ruPrimaryText: 'Сделай лабораторную страницу с 2 предметами и одним выводом.',
      enHelpText: 'Make a lab page: draw 2 items, label them, write one conclusion.',
      answer: 'Open-ended. Accept if Anita draws, labels, and makes one comparison.'
    },
    {
      id: 'speech-lab-explain',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЯ измерила два предмета.\nЯ сравнила результат.\nМой вывод: ______ длиннее.',
      ruPrimaryText: 'Объясни родителю по-русски результат измерения и вывод.',
      enHelpText: 'Tell a parent in Russian: I measured two items. I compared the result. My conclusion: ___ is longer.',
      answer: 'Accept if Anita explains the measurement in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 3 фразы.',
    '3. Реши 2 math-задачи.',
    '4. Измерь или сравни 2 безопасных предмета.',
    '5. Нарисуй лабораторную страницу.',
    '6. Напиши один вывод.',
    '7. Объясни родителю результат.'
  ],
  printWorksheet: {
    title: 'Измеряем мир / Measuring the World',
    subtitle: 'Неделя 3 · День 1 / Week 3 · Day 1',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Миссия: Нимни открывает лабораторию измерений. / Nimny opens the measurement lab.' },
          { type: 'list', items: [
            'Слова дня (8 слов). / Words of the day (8 words).',
            'Маленькое чтение: 3 фразы. / Short reading: 3 sentences.',
            'English help мелким шрифтом — только если трудно. / English help in small print — only if hard.'
          ] }
        ]
      },
      {
        title: 'Математика измерений / Measurement Math',
        blocks: [
          { type: 'text', text: 'Таблицы измерений и вопросы. / Measurement tables and questions.' },
          { type: 'list', items: [
            'Таблица 1: карандаш 14 см, книга 22 см, ложка 16 см. / Table 1.',
            'Таблица 2: чашка 9 см, коробка 18 см, тетрадь 24 см. / Table 2.',
            'Вопросы на сложение, вычитание и сравнение. / Add, subtract and compare.'
          ] }
        ]
      },
      {
        title: 'Лабораторная страница / Lab Page',
        blocks: [
          { type: 'text', text: 'Маленький опыт и рисунок. / Small experiment and drawing.' },
          { type: 'list', items: [
            'Измерь или сравни 2 безопасных предмета с родителем. / Measure or compare 2 safe objects with a parent.',
            'Правописание: длина, масса, вывод, линейка. / Spelling.',
            'Нарисуй 2 предмета и подпиши их. / Draw 2 items and label them.',
            'Напиши один вывод. / Write one conclusion.',
            'Parent Check.',
            'Notebook Mode fallback — если нет принтера, всё то же в тетради. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что измерение помогает узнать не «на глаз», а точнее.',
    mathGoal: 'Сложение, вычитание, сравнение чисел в пределах 30, работа с таблицей.',
    russianGoal: 'Прочитать слова измерить, длина, масса, объём, линейка, сантиметр, сравнить, вывод.',
    readingWritingGoal: 'Прочитать 3 фразы, написать 4 слова, подписать лабораторный рисунок.',
    practicalGoal: 'Измерить или сравнить 2 безопасных предмета и сделать вывод.',
    parentTheoryCard: {
      title: 'Как объяснить измерение ребёнку',
      purpose: 'Помочь родителю объяснить, что измерение — это способ узнать результат точнее, чем просто «мне кажется».',
      keyMessage: 'Учёный наблюдает, измеряет, записывает и делает вывод.',
      parentScript: [
        '«Когда мы просто смотрим, мы можем ошибиться.»',
        '«Когда мы измеряем, мы получаем число.»',
        '«Когда мы записываем число, мы можем сравнить.»',
        '«Вывод — это то, что мы поняли после измерения.»'
      ],
      practiceSteps: [
        'Покажите линейку и сантиметры.',
        'Измерьте один предмет вместе.',
        'Попросите Аниту измерить или сравнить второй предмет.',
        'Запишите результат и сделайте один вывод.'
      ],
      doNotSay: [
        'Не требовать идеальной точности.',
        'Не ругать, если линейка приложена не идеально.',
        'Не превращать опыт в контрольную.'
      ],
      paperOnlyNotes: [
        'Можно выполнять в тетради без принтера.',
        'Можно использовать безопасные предметы: карандаш, книга, ложка, коробка.',
        'Не использовать стекло, острые предметы или тяжёлые предметы.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится измерять, записывать результат и делать простой вывод.',
    keyMessage: 'Измерение помогает думать точнее: я вижу число, сравниваю и делаю вывод.',
    howToExplain: 'Скажите: «Если мы не измеряем, мы только угадываем. Если измеряем, у нас появляется доказательство.»',
    exampleDialogue:
      'Parent: Что длиннее — карандаш или книга?\nChild: Книга.\nParent: Как мы можем проверить?\nChild: Измерить.\nParent: Отлично. Какой вывод?\nChild: Книга длиннее карандаша.',
    readingSupport: 'Слова «измерить», «сантиметр», «сравнить» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Я измерила два предмета. Мой вывод: ___ длиннее.»',
    mathAnswerKey:
      'math-length-compare: 1) Книга. 2) Карандаш. 3) 8 см. 4) 2 см.\nmath-table-results: 1) 27 см. 2) 6 см. 3) 30 см.',
    spellingAnswerKey: 'длина\nмасса\nвывод\nлинейка',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка чтения 8 русских слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка чтения 3 коротких фраз.' },
      { taskId: 'math-length-compare', answer: 'книга; карандаш; 8 см; 2 см.' },
      { taskId: 'math-table-results', answer: '27 см; 6 см; 30 см.' },
      { taskId: 'science-measure-home', answer: 'Открытое сравнение 2 безопасных предметов.' },
      { taskId: 'spelling-measure', answer: 'длина, масса, вывод, линейка.' },
      { taskId: 'creative-lab-page', answer: 'Рисунок 2 предметов, подписи и один вывод.' },
      { taskId: 'speech-lab-explain', answer: 'Объяснение результата по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита решила math tasks.',
      'Анита измерила или сравнила 2 предмета.',
      'Анита сделала лабораторную страницу.',
      'Анита сказала вывод по-русски.'
    ],
    spellingCheck: 'Проверить длина, масса, вывод, линейка.',
    readingCheck: 'Главное — попытка русского чтения и понимание смысла.',
    practicalCheck: 'Результат может быть приблизительным. Важно: сравнение + вывод.',
    drawingCriteria: 'Есть 2 предмета, подписи и один вывод.',
    commonMistakes: [
      '«Учёный просто смотрит.» Правильно: «Учёный наблюдает, измеряет и записывает.»',
      '«Если я ошиблась, опыт плохой.» Правильно: «Ошибка помогает измерить лучше.»',
      '«Вывод — это длинный текст.» Правильно: «Вывод может быть коротким: книга длиннее карандаша.»'
    ],
    discussionPrompts: [
      'Почему число помогает сравнивать?',
      'Что легче: угадывать или измерять?',
      'Где в жизни нужны измерения?'
    ],
    ifEasy: 'Пусть Анита измерит 3 предмета и расположит их от самого короткого к самому длинному.',
    ifDifficult: 'Сравнить без линейки: длиннее, короче, примерно одинаково.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратную лабораторную страницу или ясный вывод.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не требовать идеальной научной точности. Цель — понять процесс: измерить, записать, сравнить, сделать вывод.',
    safetyNotes: 'Использовать только безопасные предметы. Не использовать стекло, ножи, острые инструменты, горячие предметы или тяжёлые предметы.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-lab-page', label: 'Бонус за аккуратную лабораторную страницу', amount: 1 },
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-length-compare',
      'math-table-results',
      'science-measure-home',
      'spelling-measure',
      'creative-lab-page',
      'speech-lab-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-3-day-2'
};

export default day1;
